import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Book } from '../types/book';
import { collection, doc, getDocs, setDoc, deleteDoc, query, where, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './FirebaseAuthContext';
import { deleteObject, ref } from 'firebase/storage';
import { storage } from '../lib/firebase';

interface LibraryContextType {
  books: Book[];
  loading: boolean;
  addBook: (book: Omit<Book, 'id'>) => Promise<Book>;
  deleteBook: (bookId: string) => Promise<void>;
  updateBook: (bookId: string, updates: Partial<Book>) => Promise<void>;
}

const LibraryContext = createContext<LibraryContextType | undefined>(undefined);

export const LibraryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBooks = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const booksRef = collection(db, 'books');
      const q = query(booksRef, where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);
      
      const fetchedBooks = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Book[];

      setBooks(fetchedBooks);
    } catch (error) {
      console.error('Error fetching books:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  const addBook = async (book: Omit<Book, 'id'>): Promise<Book> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const bookRef = doc(collection(db, 'books'));
      const newBook: Book = {
        ...book,
        id: bookRef.id,
        userId: user.uid,
        progress: 0,
        addedAt: new Date().toISOString()
      };

      await setDoc(bookRef, newBook);
      setBooks(prev => [...prev, newBook]);
      return newBook;
    } catch (error) {
      console.error('Error adding book:', error);
      throw error;
    }
  };

  const deleteBook = async (bookId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const bookRef = doc(db, 'books', bookId);
      const bookDoc = await getDoc(bookRef);
      const bookData = bookDoc.data();

      if (bookData) {
        const fileRef = ref(storage, bookData.fileUrl);
        try {
          await deleteObject(fileRef);
        } catch (error: any) {
          if (error.code !== 'storage/object-not-found') throw error;
        }

        if (bookData.coverUrl && !bookData.coverUrl.includes('default-book-cover.png')) {
          const coverRef = ref(storage, bookData.coverUrl);
          try {
            await deleteObject(coverRef);
          } catch (error: any) {
            if (error.code !== 'storage/object-not-found') throw error;
          }
        }
      }

      await deleteDoc(bookRef);
      setBooks(prev => prev.filter(book => book.id !== bookId));
    } catch (error) {
      console.error('Error deleting book:', error);
      throw error;
    }
  };

  const updateBook = async (bookId: string, updates: Partial<Book>): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const bookRef = doc(db, 'books', bookId);
      await updateDoc(bookRef, updates);
      setBooks(prev => prev.map(book => 
        book.id === bookId ? { ...book, ...updates } : book
      ));
    } catch (error) {
      console.error('Error updating book:', error);
      throw error;
    }
  };

  return (
    <LibraryContext.Provider value={{
      books,
      loading,
      addBook,
      deleteBook,
      updateBook
    }}>
      {children}
    </LibraryContext.Provider>
  );
};

export const useLibrary = () => {
  const context = useContext(LibraryContext);
  if (context === undefined) {
    throw new Error('useLibrary must be used within a LibraryProvider');
  }
  return context;
}; 